import { IRegEdSubmissionMetadata, RegEdComplianceContentType, submitRegEdCompliance } from '@Queries/RegEdCompliance';
import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import * as React from 'react';
import { v4 as uuid } from 'uuid';
import { IContentCalendarSelectedSuggestion } from '../../../../../../models';
import { useUserSession } from '../../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../../models/Logging';
import { getUniqueIdForSuggestion } from '../../../../../../models/UiUtils';
import { ISelectOption } from '../../../../../components/DeprecatedSelect';

export function useSocialMediaTargetOptions({ user }: { user?: Api.IUser }) {
	const options = React.useMemo(() => {
		const facebookOptions: ISelectOption<Api.ISocialMediaConnection>[] = [];
		const instagramOptions: ISelectOption<Api.ISocialMediaConnection>[] = [];
		const linkedInOptions: ISelectOption<Api.ISocialMediaConnection>[] = [];

		user?.socialMediaConnectedAccounts?.forEach(connection => {
			const option: ISelectOption<Api.ISocialMediaConnection> = {
				text: connection.userName,
				id: uuid(),
				dataContext: connection,
			};
			switch (connection.type) {
				case Api.SocialMediaType.Facebook:
					facebookOptions.push(option);
					break;
				case Api.SocialMediaType.Instagram:
					instagramOptions.push(option);
					break;
				case Api.SocialMediaType.LinkedIn:
					linkedInOptions.push(option);
					break;
				default: {
					break;
				}
			}
		});

		return {
			facebookOptions,
			instagramOptions,
			linkedInOptions,
		};
	}, [user]);

	return options;
}

export type ScheduleSocialMediaSuggestionsParams = {
	forUserId?: string;
	impersonationContext?: Api.IImpersonationContext;
	sendWithCompliance?: boolean;
	suggestions: IContentCalendarSelectedSuggestion[];
	templates: Api.ITemplate[];
	designatedTargets: Api.IPostTarget[];
	sendWithComplianceEmail?: string;
	generatedContent?: Record<string, Api.IRawRichTextContentState>;
	generatedAiReferences?: Record<string, Api.IAIReference>;
	editedContent?: Record<string, Api.IRawRichTextContentState>;
	logoSizes?: Record<string, number>;
	regEdMetadata?: IRegEdSubmissionMetadata;
};

export function useScheduleSocialMediaSuggestions({
	onError,
	onSuccess,
}: {
	onError?: (error: Api.IOperationResultNoValue) => void;
	onSuccess?: (
		result: PromiseSettledResult<Api.ISocialMediaPost>[],
		params: ScheduleSocialMediaSuggestionsParams
	) => void;
}) {
	const userSession = useUserSession();
	const { logApiError } = useEventLogging('MultiSocialScheduler');
	return useMutation({
		mutationFn: ({
			designatedTargets,
			impersonationContext,
			forUserId,
			templates,
			sendWithCompliance,
			sendWithComplianceEmail,
			suggestions,
			generatedContent,
			generatedAiReferences,
			editedContent,
			logoSizes,
			regEdMetadata,
		}: ScheduleSocialMediaSuggestionsParams) => {
			const postImpersonationContext = Api.ImpersonationBroker.withForUser(impersonationContext, forUserId);
			return Promise.allSettled(
				suggestions.map(async suggestion => {
					const template = templates.find(t => t.id === suggestion.templateReference.templateId);
					let aiGeneratedContent = null;
					const editedContentForSuggestion = editedContent[getUniqueIdForSuggestion(suggestion)];
					if (generatedContent) {
						aiGeneratedContent = generatedContent[getUniqueIdForSuggestion(suggestion)];
					}

					const aiReference = generatedAiReferences?.[getUniqueIdForSuggestion(suggestion)];
					const request: Api.ICreateSocialMediaPostRequest = {
						sendWithComplianceEmail,
						designatedTargets,
						forUserId,
						name: template.name,
						content: editedContentForSuggestion || aiGeneratedContent || template.content,
						templateReference: suggestion.templateReference,
						sendWithCompliance,
						// Callers must pre-merge editedImages into template.attachments before invoking this mutation
						attachments: template.attachments,
						dueDate: suggestion.schedule.startDate,
						overlayLogo: suggestion.overlayLogo || false,
						logoSize: suggestion.overlayLogo ? logoSizes?.[getUniqueIdForSuggestion(suggestion)] : undefined,
						aiReference,
					};

					const post = await userSession.webServiceHelper.callAsync<Api.ISocialMediaPost>(
						Api.ImpersonationBroker.composeApiUrl({
							urlPath: 'social/post',
							queryParams: {
								contentCalendarSuggestionId: suggestion.id,
								forUserId,
							},
							// Impersonate the post's creator (user impersonation), not just the account.
							impersonationContext: postImpersonationContext,
						}),
						'POST',
						request
					);

					// Isolated: the post already exists, so a submission failure must not fail the created post.
					await submitRegEdCompliance({
						userSession,
						parentType: RegEdComplianceContentType.SocialMediaPost,
						parentId: post.id,
						approvalRequestId: post.approvalRequests?.[0]?.id,
						metadata: regEdMetadata,
						impersonationContext: postImpersonationContext,
						logApiError,
					});

					return post;
				})
			);
		},
		onError,
		onSuccess,
	});
}
